import { apiRequest } from './api'

/**
 * Audit logs service for managing audit log operations
 */
export const auditService = {
  /**
   * Search audit logs with advanced filtering
   * @param {Object} searchParams - Search parameters
   * @returns {Promise} API response with audit logs
   */
  async searchLogs(searchParams) {
    // Filter out empty string values to avoid API validation errors
    const cleanParams = Object.entries(searchParams).reduce((acc, [key, value]) => {
      // Only include non-empty values (but allow null, 0, false)
      if (value !== '' && value !== undefined) {
        acc[key] = value
      }
      return acc
    }, {})

    // If log_type is 'all', query all log types and combine results
    if (cleanParams.log_type === 'all') {
      const logTypes = ['user', 'data_input', 'workflow', 'security', 'system']
      const limitPerType = Math.ceil((cleanParams.limit || 20) / logTypes.length)

      // Create search requests for each log type
      const searchPromises = logTypes.map(logType =>
        apiRequest.post('/audit/search', {
          ...cleanParams,
          log_type: logType,
          limit: limitPerType
        }).catch(error => {
          console.warn(`Failed to fetch ${logType} logs:`, error)
          return { data: { logs: [], total_count: 0, has_next: false, has_previous: false } }
        })
      )

      // Execute all searches in parallel
      const responses = await Promise.all(searchPromises)

      // Combine and sort results
      const allLogs = responses.flatMap(response => response.data.logs || [])
      const sortedLogs = allLogs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

      // Apply the original limit to the combined results
      const skip = cleanParams.skip || 0
      const limit = cleanParams.limit || 20
      const limitedLogs = sortedLogs.slice(skip, skip + limit)

      return {
        logs: limitedLogs,
        total_count: allLogs.length,
        has_next: sortedLogs.length > skip + limit,
        has_previous: skip > 0
      }
    }

    // For specific log types, use the original implementation
    const response = await apiRequest.post('/audit/search', cleanParams)
    return response.data
  },

  /**
   * Get audit statistics for a specific log type
   * @param {Object} statsParams - Statistics parameters
   * @returns {Promise} API response with statistics
   */
  async getStatistics(statsParams) {
    const response = await apiRequest.post('/audit/statistics', statsParams)
    return response.data
  },

  /**
   * Get user activity summary
   * @param {number} userId - User ID
   * @param {number} days - Number of days to look back
   * @returns {Promise} API response with user activity
   */
  async getUserActivity(userId, days = 30) {
    const response = await apiRequest.get(`/audit/users/${userId}/activity?days=${days}`)
    return response.data
  },

  /**
   * Get recent activities for dashboard
   * @param {number} limit - Number of activities to fetch (default: 4)
   * @param {string} userRole - User role to determine filtering
   * @param {number} userId - User ID for role-based filtering
   * @returns {Promise} API response with recent activities
   */
  async getRecentActivities(limit = 4, userRole = null, userId = null) {
    // Get activities from both user and data_input log types to show comprehensive activity
    const userParams = {
      log_type: 'user',
      skip: 0,
      limit: Math.ceil(limit / 2) // Split the limit between log types
    }

    const dataInputParams = {
      log_type: 'data_input',
      skip: 0,
      limit: Math.ceil(limit / 2)
    }

    // For area_manager and user roles, only show their own activities
    if (userRole === 'area_manager' || userRole === 'user') {
      userParams.user_id = userId
      dataInputParams.user_id = userId
    }

    // Fetch both log types and combine results
    const [userResponse, dataInputResponse] = await Promise.all([
      apiRequest.post('/audit/search', userParams),
      apiRequest.post('/audit/search', dataInputParams)
    ])

    // Combine and sort by created_at descending
    const combinedLogs = [
      ...(userResponse.data.logs || []),
      ...(dataInputResponse.data.logs || [])
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

    // Return in the same format as the original API
    return {
      logs: combinedLogs.slice(0, limit), // Limit to requested number
      total_count: combinedLogs.length,
      has_next: false,
      has_previous: false
    }
  },

  /**
   * Get resource audit trail
   * @param {Object} trailParams - Trail parameters
   * @returns {Promise} API response with audit trail
   */
  async getAuditTrail(trailParams) {
    const response = await apiRequest.post('/audit/trail', trailParams)
    return response.data
  },

  /**
   * Get security events
   * @param {Object} params - Security event parameters
   * @returns {Promise} API response with security events
   */
  async getSecurityEvents(params = {}) {
    const { severity = 'medium', hours = 24, skip = 0, limit = 50 } = params

    // Build query parameters, only including non-empty values
    const queryParams = new URLSearchParams()
    if (severity && severity !== '') queryParams.append('severity', severity)
    if (hours) queryParams.append('hours', hours)
    if (skip) queryParams.append('skip', skip)
    if (limit) queryParams.append('limit', limit)

    const response = await apiRequest.get(`/audit/security/events?${queryParams.toString()}`)
    return response.data
  },

  /**
   * Get audit configuration
   * @returns {Promise} API response with audit configuration
   */
  async getConfiguration() {
    const response = await apiRequest.get('/audit/configuration')
    return response.data
  },

  /**
   * Export audit logs
   * @param {Object} exportParams - Export parameters
   * @returns {Promise} API response with export data
   */
  async exportLogs(exportParams) {
    const response = await apiRequest.post('/audit/export', exportParams)
    return response.data
  },

  /**
   * Download exported audit logs
   * @param {string} exportId - Export ID
   * @param {string} format - Export format (csv, json)
   * @returns {Promise} Blob response for download
   */
  async downloadExport(exportId, format = 'csv') {
    const response = await apiRequest.get(`/audit/download/${exportId}?format=${format}`, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Get audit statistics
   * @param {Object} params - Statistics parameters
   * @returns {Promise} API response with statistics data
   */
  async getStatistics(params) {
    const response = await apiRequest.post('/audit/statistics', params)
    return response.data
  },

  /**
   * Get user activity summary
   * @param {Object} params - Activity parameters
   * @returns {Promise} API response with user activity data
   */
  async getUserActivity(params) {
    const response = await apiRequest.post('/audit/user-activity', params)
    return response.data
  },

  /**
   * Cleanup old audit logs
   * @param {Object} params - Cleanup parameters
   * @returns {Promise} API response with cleanup results
   */
  async cleanupLogs(params) {
    const response = await apiRequest.post('/audit/cleanup', null, {
      params: {
        log_type: params.log_type,
        retention_days: params.retention_days
      }
    })
    return response.data
  },
}

/**
 * Audit log types enum
 */
export const AUDIT_LOG_TYPES = {
  ALL: 'all',
  USER: 'user',
  DATA_INPUT: 'data_input',
  WORKFLOW: 'workflow',
  SECURITY: 'security',
  SYSTEM: 'system'
}

/**
 * Audit actions enum
 */
export const AUDIT_ACTIONS = {
  CREATE: 'create',
  READ: 'read',
  UPDATE: 'update',
  DELETE: 'delete',
  LOGIN: 'login',
  LOGOUT: 'logout',
  APPROVE: 'approve',
  REJECT: 'reject',
  EXPORT: 'export',
  IMPORT: 'import'
}

/**
 * Audit severity levels enum
 */
export const AUDIT_SEVERITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
}

/**
 * Helper function to format audit log display
 * @param {Object} log - Audit log object
 * @returns {Object} Formatted log for display
 */
export const formatAuditLog = (log) => {
  return {
    ...log,
    formattedDate: new Date(log.created_at).toLocaleString(),
    severityColor: getSeverityColor(log.severity),
    actionIcon: getActionIcon(log.action),
    statusBadge: log.success ? 'success' : 'error'
  }
}

/**
 * Get color class for severity level
 * @param {string} severity - Severity level
 * @returns {string} CSS color class
 */
export const getSeverityColor = (severity) => {
  switch (severity) {
    case AUDIT_SEVERITY.LOW:
      return 'text-green-600 bg-green-100'
    case AUDIT_SEVERITY.MEDIUM:
      return 'text-yellow-600 bg-yellow-100'
    case AUDIT_SEVERITY.HIGH:
      return 'text-orange-600 bg-orange-100'
    case AUDIT_SEVERITY.CRITICAL:
      return 'text-red-600 bg-red-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

/**
 * Get icon for action type
 * @param {string} action - Action type
 * @returns {string} Icon name
 */
export const getActionIcon = (action) => {
  switch (action) {
    case AUDIT_ACTIONS.CREATE:
      return 'Plus'
    case AUDIT_ACTIONS.UPDATE:
      return 'Edit'
    case AUDIT_ACTIONS.DELETE:
      return 'Trash2'
    case AUDIT_ACTIONS.LOGIN:
      return 'LogIn'
    case AUDIT_ACTIONS.LOGOUT:
      return 'LogOut'
    case AUDIT_ACTIONS.APPROVE:
      return 'Check'
    case AUDIT_ACTIONS.REJECT:
      return 'X'
    case AUDIT_ACTIONS.EXPORT:
      return 'Download'
    case AUDIT_ACTIONS.IMPORT:
      return 'Upload'
    default:
      return 'Activity'
  }
}